import React, { useEffect, useRef, useState } from "react";
import { Download, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CHANNEL_COLOR, downloadCsv, num, selectionParams, usd } from "./networkFmt";

const PAGE = 50;
const sel_ = "bg-[#0a0a0a] border border-white/15 rounded-md text-xs px-2 py-1.5 text-white/80";

function CallRow({ c, i }) {
    return (
        <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`network-call-row-${i}`}>
            <td className="px-3 py-2 font-mono text-[10px] text-white/50 whitespace-nowrap">{(c.at || "").replace("T", " ").slice(0, 19)}</td>
            <td className="px-3 py-2 text-xs whitespace-nowrap">
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: CHANNEL_COLOR[c.channel] || CHANNEL_COLOR.other }} />
                {c.server_name || c.server}<span className="text-white/35"> · {c.server_label}</span>
            </td>
            <td className="px-3 py-2 text-xs truncate max-w-[10rem]">{c.client_name || c.client_id || <span className="text-white/30">—</span>}</td>
            <td className="px-3 py-2 text-xs whitespace-nowrap">{c.feature}{c.kind === "reuse" && <span className="ml-1.5 text-[9px] uppercase border border-white/20 rounded px-1">reuse</span>}</td>
            <td className="px-3 py-2 text-xs text-white/70 truncate max-w-[18rem]" title={c.call_detail}>
                {c.call_detail && c.call_detail !== c.feature ? c.call_detail : <span className="text-white/30">—</span>}
            </td>
            <td className="px-3 py-2 font-mono text-[10px] text-white/50 whitespace-nowrap">{c.model || "—"}</td>
            <td className="px-3 py-2 font-mono text-[10px] text-right text-white/50 whitespace-nowrap">{c.kind === "ai" ? `${num(c.tokens_in)} / ${num(c.tokens_out)}` : `${num(c.docs_count)} docs`}</td>
            <td className="px-3 py-2 font-mono text-xs text-right whitespace-nowrap" data-testid={`network-call-cost-${c.call_id}`}>
                {usd(c.cost_usd, 4)}
                {c.kind === "reuse" && c.free && <span className="ml-1.5 text-[9px] uppercase border border-emerald-400/40 text-emerald-300 rounded px-1">free</span>}
                {c.kind === "reuse" && c.saved_usd > 0 && <div className="text-[9px] text-emerald-300/70">saved {usd(c.saved_usd, 4)}</div>}
            </td>
        </tr>
    );
}

/** Every individual AI call (and reuse charge) behind the figures, from any server. */
export default function NetworkCallList({ sel, features, feature, onFeature }) {
    const [kind, setKind] = useState("");
    const [res, setRes] = useState(null);
    const params = selectionParams(sel, { feature, kind });
    const key = params.toString();
    const [page, setPage] = useState({ key, offset: 0 });
    const offset = page.key === key ? page.offset : 0;
    const setOffset = (o) => setPage({ key, offset: o });
    const seq = useRef(0);

    useEffect(() => {
        const n = ++seq.current;
        api.get(`/admin/billing/network/calls?${key}&limit=${PAGE}&offset=${offset}`)
            .then((r) => { if (n === seq.current) setRes(r.data); })
            .catch((e) => toast.error(formatApiErrorDetail(e)));
    }, [key, offset]);

    const exportCalls = () => downloadCsv("/admin/billing/network/calls.csv", params, `network-cost-calls_${sel.dateFrom}_${sel.dateTo}.csv`)
        .then(() => toast.success("Call list CSV downloaded")).catch((e) => toast.error(e.message));
    const total = res?.total || 0;

    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md" data-testid="network-call-list">
            <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
                <ListOrdered className="w-3.5 h-3.5 text-[#7C3AED]" /><div className="font-display text-sm">Call list</div>
                <span className="text-[10px] font-mono text-white/40" data-testid="network-calls-total">{num(total)} rows · {res?.selection?.label || ""}</span>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <select value={feature} onChange={(e) => onFeature(e.target.value)} className={sel_} data-testid="network-calls-feature" aria-label="Feature">
                        <option value="">All features</option>
                        {feature && !features.includes(feature) && <option value={feature}>{feature}</option>}
                        {features.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select value={kind} onChange={(e) => setKind(e.target.value)} className={sel_} data-testid="network-calls-kind" aria-label="Type">
                        <option value="">AI calls + reuse charges</option><option value="ai">AI calls only</option><option value="reuse">Reuse charges only</option>
                    </select>
                    <button onClick={exportCalls} data-testid="network-calls-export" className="px-3 py-1.5 border border-white/15 rounded-md text-xs hover:bg-white/5 flex items-center gap-1.5">
                        <Download className="w-3.5 h-3.5" /> Calls CSV
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                    <thead><tr className="text-left label-xs border-b border-white/10">
                        {["Time (UTC)", "Server", "Client", "Feature", "Call detail", "Model"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}
                        <th className="px-3 py-2 font-normal text-right">Tokens in / out</th><th className="px-3 py-2 font-normal text-right">Cost</th>
                    </tr></thead>
                    <tbody>{(res?.items || []).map((c, i) => <CallRow key={c.call_id} c={c} i={i} />)}</tbody>
                </table>
                {res && res.items.length === 0 && <div className="p-6 text-center text-xs text-white/40" data-testid="network-calls-empty">No calls match these filters.</div>}
            </div>
            <div className="px-4 py-2 flex items-center justify-end gap-2 text-[11px] text-white/50">
                <span>{total ? `${offset + 1}–${Math.min(offset + PAGE, total)} of ${num(total)}` : ""}</span>
                <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="network-calls-prev" className="px-2 py-1 border border-white/10 rounded disabled:opacity-30">Prev</button>
                <button disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} data-testid="network-calls-next" className="px-2 py-1 border border-white/10 rounded disabled:opacity-30">Next</button>
            </div>
        </div>
    );
}
